import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../contexts/AuthContext";
import { Play, TrendingUp, Clock, MessageCircle, Upload } from "lucide-react";

const SORT_OPTIONS = ["Newest", "Trending", "Most Discussed"];
const CATEGORIES = ["All", "Music", "DJ Sets", "Film", "Business", "Gaming", "Previously Recorded"];

function ContentCard({ item }) {
  return (
    <Link
      to={`/content/${item.id}`}
      data-testid={`content-card-${item.id}`}
      className="group bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:bg-[#1a1a1a] hover:border-[#00ff77]/30 hover:shadow-[0_0_20px_rgba(0,255,119,0.1)] transition-all duration-300"
    >
      <div className="relative aspect-video bg-[#1a1a1a] overflow-hidden">
        {item.thumbnailURL ? (
          <img src={item.thumbnailURL} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Play size={32} className="text-zinc-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-[#00ff77] flex items-center justify-center">
            <Play size={18} className="text-black ml-0.5" fill="black" />
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-white text-sm font-semibold line-clamp-2 mb-1">{item.title}</h3>
        <p className="text-zinc-500 text-xs truncate">{item.creatorName || "Creator"}</p>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-zinc-600 text-xs">{item.views || 0} views</span>
          {item.category && (
            <span className="text-zinc-600 text-xs bg-white/5 px-2 py-0.5 rounded-full">{item.category}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const { user, isCreator } = useAuth();
  const [featured, setFeatured] = useState(null);
  const [feed, setFeed] = useState([]);
  const [sort, setSort] = useState("Newest");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, [sort]);

  const loadContent = async () => {
    setLoading(true);
    try {
      // Load featured premiere
      const featuredQ = query(
        collection(db, "featured"),
        where("active", "==", true),
        orderBy("order", "asc"),
        limit(1)
      );
      const featuredSnap = await getDocs(featuredQ);
      if (!featuredSnap.empty) {
        const featuredDoc = featuredSnap.docs[0].data();
        const contentDoc = await getDoc(doc(db, "content", featuredDoc.contentId));
        if (contentDoc.exists()) {
          setFeatured({ id: contentDoc.id, ...contentDoc.data() });
        }
      }

      // Fetch all content and sort in JS — avoids Firestore excluding docs without fields
      const feedSnap = await getDocs(query(collection(db, "content"), limit(100)));
      const all = feedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = all.filter((d) => !d.status || d.status === "approved");

      if (sort === "Newest") {
        filtered.sort((a, b) => (b.createdAt?.seconds || b.createdAt || 0) - (a.createdAt?.seconds || a.createdAt || 0));
      } else if (sort === "Trending") {
        filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
      } else {
        filtered.sort((a, b) => (b.commentCount || 0) - (a.commentCount || 0));
      }
      setFeed(filtered.slice(0, 30));
    } catch (err) {
      console.error("Error loading feed:", err);
      setFeed([]);
    }
    setLoading(false);
  };

  return (
    <div data-testid="home-page" className="space-y-8 animate-fade-in">
      {/* Hero / Latest Premiere */}
      {featured && (
        <section
          data-testid="featured-premiere"
          className="relative rounded-2xl overflow-hidden bg-[#111] border border-white/5"
          style={{ minHeight: 240 }}
        >
          <div className="absolute inset-0">
            {featured.thumbnailURL && (
              <img src={featured.thumbnailURL} alt="" className="w-full h-full object-cover opacity-30" />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent" />
          </div>
          <div className="relative z-10 p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex-1">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#00ff77] mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00ff77] animate-pulse" />
                Latest Premiere
              </span>
              <h1 className="text-2xl sm:text-3xl font-black text-white mb-2 leading-tight">{featured.title}</h1>
              <p className="text-zinc-400 text-sm mb-1">
                by <Link to={`/c/${featured.creatorSlug}`} className="text-white hover:text-[#00ff77] transition-colors">{featured.creatorName}</Link>
                {featured.views > 0 && <span className="text-zinc-600"> — {featured.views} views</span>}
              </p>
              <Link
                to={`/content/${featured.id}`}
                data-testid="watch-now-btn"
                className="inline-flex items-center gap-2 mt-4 bg-[#00ff77] text-black font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#00e66b] transition-colors"
              >
                <Play size={14} fill="black" />
                Watch Now
              </Link>
            </div>
            {featured.thumbnailURL && (
              <div className="hidden sm:block w-64 aspect-video rounded-xl overflow-hidden border border-white/10 flex-shrink-0">
                <img src={featured.thumbnailURL} alt={featured.title} className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Feed */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Your Feed</h2>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-xs">Sort:</span>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-[#111] border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#00ff77]/50"
            >
              {SORT_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#111] rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-video bg-[#1a1a1a]" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-[#1a1a1a] rounded w-3/4" />
                  <div className="h-3 bg-[#1a1a1a] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : feed.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {feed.map((item) => <ContentCard key={item.id} item={item} />)}
          </div>
        ) : (
          <div className="text-center py-16 text-zinc-500">
            <Play size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No content yet. Be the first to premiere.</p>
            {user ? (
              isCreator ? (
                <Link
                  to="/dashboard/upload"
                  data-testid="upload-content-cta"
                  className="inline-flex items-center gap-2 mt-4 text-xs font-semibold text-[#00ff77] border border-[#00ff77]/30 px-4 py-2 rounded-full hover:bg-[#00ff77]/10 transition-colors"
                >
                  <Upload size={12} /> Upload Content
                </Link>
              ) : (
                <Link
                  to="/apply"
                  data-testid="become-creator-cta"
                  className="inline-block mt-4 text-xs text-[#00ff77] hover:underline"
                >
                  Become a Creator
                </Link>
              )
            ) : (
              <Link
                to="/auth?mode=register"
                data-testid="join-cta"
                className="inline-block mt-4 text-xs text-[#00ff77] hover:underline"
              >
                Join StagePass
              </Link>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
